<template>
  <div
    class="rounded border p-3 single-variant-addon-container"
    v-if="items.length"
  >
    <b-table
      :items="transformedItems()"
      :fields="fields"
      class="border"
      hover
      fixed
    >
      <template slot="sku" slot-scope="data">
        <b-form-group
          :state="validateState('sku', data.index)"
          :invalid-feedback="formErrors[data.index].variable.sku"
        >
          <b-form-input
            :id="`variant_sku_${data.index}`"
            v-model="data.item.variable.sku"
            @keyup.native="trimSpaces($event)"
          >
          </b-form-input>
        </b-form-group>
      </template>
      <template slot="regular_price" slot-scope="data">
        <b-form-group
          :state="validateState('regular_price', data.index)"
          :invalid-feedback="formErrors[data.index].variable.regular_price"
        >
          <b-input-group :id="`variant-price-input-group-${data.index}`">
            <b-input-group-text class="border-right-0 bg-transparent"
              >₱</b-input-group-text
            >
            <b-form-input
              type="number"
              :id="`variant_price_${data.index}`"
              class="border-left-0"
              v-model="data.item.variable.regular_price"
              @keyup="handleInput(`${data.index}`)"
            >
            </b-form-input>
          </b-input-group>
        </b-form-group>
      </template>
      <template slot="action" slot-scope="data">
        <img
          v-if="data.item.variable.uuid"
          :id="`delete-variant-option-${data.index}`"
          src="@/assets/images/actions/delete.svg"
          :alt="`Delete Variant Option ${data.index}`"
          style="cursor: pointer"
          @click="
            handleDeleteProductInventory({
              index: data.index,
              uuid: data.item.variable.uuid,
            })
          "
        />
      </template>
    </b-table>
    <b-button
      variant="single-manage"
      class="border-0 text-primary font-weight-bold"
      @click="manageVariantAddOn"
      v-if="items.length"
    >
      Manage Variants
    </b-button>
  </div>
</template>

<script>
import { mapState } from "vuex";
import pvg from "@/services/product.variance.generator";
import { required } from "vuelidate/lib/validators";
import _ from "lodash";
import { productsMixins } from "@/mixins/products_mixins";

export default {
  props: {
    mode: String,
    items: Array,
    storedInventory: Array,
  },
  data() {
    return {
      fields: [
        {
          key: "variant",
          label: "Variant Name",
          thClass: "bg-light text-center border-bottom-0 border-right",
          tdClass: "border-right align-middle text-truncate",
          formatter: (value, key, item) => {
            return item.variable.variation_code
              .split("_")
              .map((hash) => {
                const attribute = this.product_attributes.find((variant) =>
                  variant.variations.find((option) => option.hash === hash)
                );

                if (attribute)
                  return attribute.variations.find(
                    (option) => option.hash === hash
                  ).name;
                else return [];
              })
              .join(" | ");
          },
        },
        {
          key: "sku",
          label: "SKU",
          thClass: "bg-light text-center border-bottom-0 border-right",
          tdClass: "border-right",
        },
        {
          key: "regular_price",
          label: "Price",
          thClass: "bg-light text-center border-bottom-0",
        },
        {
          key: "action",
          label: "",
          thClass: "bg-light text-center border-bottom-0",
          thStyle: "width: 50px",
        },
      ],
      storedItems: {},
      setProductData: 0,
    };
  },
  computed: {
    ...mapState("products", [
      "product_attributes",
      "product_variables",
      "product_delete",
    ]),
    ...mapState("estoreInventories", {
      productDetails: (state) => state.product_data,
      estoreErrors: (state) => state.errors,
    }),
    apiErrors() {
      return this.estoreErrors || null;
    },
    formErrors() {
      let errors = this.product_variables.map((variant, variantIndex) => ({
        variable: {
          sku: _.has(
            this.apiErrors,
            `product_variables.${variantIndex}.variable.sku`
          )
            ? this.apiErrors[`product_variables.${variantIndex}.variable.sku`]
            : null,
          regular_price: _.has(
            this.apiErrors,
            `product_variables.${variantIndex}.variable.regular_price`
          )
            ? this.apiErrors[
                `product_variables.${variantIndex}.variable.regular_price`
              ]
            : null,
        },
      }));

      const required_fields = ["sku", "regular_price"];

      this.product_variables.forEach((item, itemIndex) => {
        Object.keys(item.variable).forEach((formKey) => {
          if (
            required_fields.includes(formKey) &&
            this.$v.product_variables.$each[itemIndex].variable[formKey]
              .$dirty &&
            !this.$v.product_variables.$each[itemIndex].variable[formKey]
              .required
          )
            errors[itemIndex].variable[formKey] = "This field is required.";
        });
      });

      return errors;
    },
    isLastVariant() {
      return !this.product_variables.length;
    },
  },
  mounted() {
    this.$root.$on("validateProductCreation", () => {
      this.$v.product_variables.$touch();
    });
    this.$root.$on(
      "setProductDataStatus",
      (value) => (this.setProductData = value)
    );
    this.$root.$on("proceedDeleteProductInventory", () => this.deleteProduct());
  },
  beforeDestroy() {
    this.$root.$off("validateProductCreation");
    this.$store.commit("products/resetForm");
    this.$root.$off("setProductDataStatus");
    this.$root.$off("proceedDeleteProductInventory");
  },
  validations: {
    product_variables: {
      $each: {
        variable: {
          sku: {
            required,
          },
          regular_price: {
            required,
          },
        },
      },
    },
  },
  mixins: [productsMixins],
  methods: {
    manageVariantAddOn() {
      this.storeSkusAndPrices();
      this.$parent.$parent.is_manage = true;
      this.$parent.$parent.create();
    },
    storeSkusAndPrices() {
      this.items
        .map((variant) => variant.variable)
        .forEach((variable) => {
          this.storedItems[variable.variation_code] = {
            sku: variable.sku,
            regular_price: Number(variable.regular_price),
          };
        });
    },
    generateVariations() {
      const known_variants = new Set();
      const variations = this.product_attributes
        .map((attribute) => attribute.variations)
        .map((variant) => variant.map((option) => option.hash.trim()));

      this.generateProductVariables(variations);
    },
    generateProductVariables(variations) {
      const product_variables = pvg
        .combineArrays(variations)
        .map((variation) => variation.split("_").filter((option) => option))
        .map((option) => {
          const formulateVariationCode = () =>
            option.map((name) => {
              return name;
            });

          const isExisting = _.flatMap(
            this.storedInventory,
            "variation_code"
          ).includes(formulateVariationCode().join("_"));

          const formulatedInventory = () => ({
            action: isExisting ? "update" : "create",
            uuid: isExisting
              ? this.storedInventory.find(
                  (item) =>
                    item.variation_code === formulateVariationCode().join("_")
                ).uuid
              : null,
          });

          return {
            action: formulatedInventory().action,
            variable: {
              variation_code: formulateVariationCode().join("_"),
              stock: 1,
              currency: "PHP",
              visibility: true,
              sku: null,
              regular_price: 1,
              on_sale: false,
              is_limited_stock: 0,
              uuid: formulatedInventory().uuid,
            },
          };
        });

      this.$store.commit("products/setProductVariables", product_variables);
    },
    validateState(name, index) {
      if (this.formErrors[index].variable[name]) return false;

      const { $dirty, $error } =
        this.$v.product_variables.$each[index].variable[name];

      return $dirty ? !$error : null;
    },
    transformedItems() {
      if (this.items.length) {
        const existing_skus_and_price = Object.keys(this.storedItems);
        return this.items.map((variant) => {
          if (
            existing_skus_and_price.includes(variant.variable.variation_code)
          ) {
            variant["variable"]["sku"] =
              this.storedItems[variant.variable.variation_code]["sku"];
            variant["variable"]["regular_price"] =
              this.storedItems[variant.variable.variation_code][
                "regular_price"
              ];
          }
          return variant;
        });
      }
    },
    handleDeleteProductInventory({ index, uuid }) {
      const delete_data = {
        index: index,
        uuid: uuid,
      };
      this.$store.commit("products/setProductDeleteInventory", delete_data);
      this.$root.$emit("setProductInventoryDelete", true);
    },
    async deleteProduct() {
      const { uuid, index } = this.product_delete;
      await this.$store.dispatch("estoreInventories/deleteProduct", {
        uuid: uuid,
      });

      if (_.isEmpty(this.estoreErrors)) {
        this.$root.$emit("setProductInventoryDelete", false);
        this.$store.commit("products/deleteProductInventory", index);
        if (this.isLastVariant) this.$router.push({ name: "Products" });
      }
    },
    handleInput(index){
      const regex = /^\d+(\.\d{1,2})?$/;
      if(!regex.test(this.product_variables[index].variable.regular_price)){
        this.product_variables[index].variable.regular_price = parseFloat(this.product_variables[index].variable.regular_price).toFixed(2)
      }
    }
  },
  watch: {
    product_attributes() {
      this.generateVariations();
      if (!this.setProductData) this.$root.$emit("updateVariables");
    },
  },
};
</script>

<style lang="scss" scoped>
[id^="variant-price-input-group-"] {
  .input-group-text {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .form-control {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}
</style>

<style lang="scss">
.single-variant-addon-container {
  .btn.btn-single-manage {
    background-color: #f5f5f5;
  }
}
</style>
